import React, { useState, useEffect } from "react";
import axios from "axios";

const services = {
  data: {
    name: "Buy Data Bundle",
    fields: [ "planType", "dataPlan", "phoneNumber"],
  },
  airtime: {
    name: "Buy Airtime",
    fields: ["network", "phoneNumber", "amount"],
  },
  electricity: {
    name: "Electricity Bill Payment",
    fields: ["meterNumber", "meterType", "amount", "disco"],
    providers: [
      { id: "IKEDC", name: "Ikeja Electric" },
      { id: "EKEDC", name: "Eko Electricity" },
      { id: "KEDCO", name: "Kano Electricity" },
      { id: "PHED", name: "Port Harcourt Electric" },
      { id: "JED", name: "Jos Electricity" },
      { id: "IBEDC", name: "Ibadan Electricity" },
      { id: "KAEDCO", name: "Kaduna Electric" },
      { id: "AEDC", name: "Abuja Electric" },
      { id: "EEDC", name: "Enugu Electric" },
      { id: "BEDC", name: "Benin Electric" },
      { id: "ABA", name: "ABA Electric" },
      { id: "YEDC", name: "YOLA Electric" }
    ]
  },
  tv: {
    name: "TV Subscription",
    fields: ["smartCardNumber", "package"],
  },
  education: {
    name: "Education",
    fields: ["phoneNumber", "emailAddress"]
  }
};

const ServiceOverlay = ({ isOpen, onClose, serviceType, setTransactionDetails, setShowReceipt }) => {
  const [formData, setFormData] = useState({});
  const [allDataPlans, setAllDataPlans] = useState([]);
  const [dataPlans, setDataPlans] = useState([]);
  const [selectedPlanType, setSelectedPlanType] = useState("");
  const [userBalance, setUserBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const service = services[serviceType];

  useEffect(() => {
    if (serviceType === "data") {
      const fetchAllDataPlans = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await axios.get(`${API_BASE_URL}/api/v1/dataplans`);
          if (response.data) {
            setAllDataPlans(response.data);
          }
        } catch (error) {
          setError("Failed to fetch data plans. Please try again.");
          setAllDataPlans([]);
        } finally {
          setLoading(false);
        }
      };
      fetchAllDataPlans();
    }
  }, [serviceType, API_BASE_URL]);

  useEffect(() => {
    if ( selectedPlanType && allDataPlans.length > 0) {
      const filteredPlans = allDataPlans.find(
        service => service.name === selectedPlanType
      );
      if (filteredPlans?.plans) {
        setDataPlans(filteredPlans.plans);
      }
    } else {
      setDataPlans([]);
    }
  }, [selectedPlanType, allDataPlans]);

  useEffect(() => {
    const fetchUserBalance = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_BASE_URL}/api/v1/auth/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setUserBalance(response.data.virtualAccount.amountAccount);
      } catch (error) {
        console.error("Failed to fetch balance:", error);
      }
    };
    
    if (isOpen) {
      fetchUserBalance();
    }
  }, [isOpen, API_BASE_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePlanTypeChange = (e) => {
    const value = e.target.value;
    setSelectedPlanType(value);
    setFormData(prev => ({ ...prev, planType: value }));
  };

  const handleDataPlanSelect = (e) => {
    const value = e.target.value;
    const selectedPlan = dataPlans.find(plan => plan.id.toString() === value);
    if (selectedPlan) {
      setFormData(prev => ({
        ...prev,
        planId: selectedPlan.id,
        amount: parseFloat(selectedPlan.reseller_amount),
        dataPlan: value
      }));
    }
  };

  const handleSubmit = async () => {
    if (serviceType === "data") {
      if (!formData.planId || !formData.phoneNumber || !formData.amount) {
        setError("Please fill in all required fields");
        return;
      }

      if (formData.amount > userBalance) {
        setError(`Insufficient balance. Required: ₦${formData.amount.toLocaleString()}, Available: ₦${userBalance.toLocaleString()}`);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.post(
          `${API_BASE_URL}/api/v1/dataplan/buy`,
          {
            planId: formData.planId,
            phoneNumber: formData.phoneNumber,
            amount: formData.amount
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data) {
          onClose();
          setTransactionDetails({
            type: 'Data Bundle',
            network: selectedPlanType,
            plan: dataPlans.find(plan => plan.id === formData.planId)?.name,
            phoneNumber: formData.phoneNumber,
            amount: formData.amount,
            date: new Date().toLocaleString(),
            reference: response.data.reference || 'N/A'
          });
          setShowReceipt(true);
        }
      } catch (error) {
        setError(error.response?.data?.error || "Transaction failed. Please try again.");
      } finally {
        setLoading(false);
      }
    } else if (serviceType === "airtime") {
      if (!formData.network || !formData.phoneNumber || !formData.amount) {
        setError("Please fill in all required fields");
        return;
      }

      if (parseFloat(formData.amount) > userBalance) {
        setError(`Insufficient balance. Required: ₦${parseFloat(formData.amount).toLocaleString()}, Available: ₦${userBalance.toLocaleString()}`);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.post(
          `${API_BASE_URL}/api/v1/buy/airtime`,
          {
            network: formData.network,
            phoneNumber: formData.phoneNumber,
            amount: parseFloat(formData.amount)
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data) {
            onClose();
            setTransactionDetails({
              type: 'Airtime',
              network: formData.network === '1' ? 'MTN' : 
                      formData.network === '2' ? 'Airtel' :
                      formData.network === '3' ? 'GLO' : 
                      formData.network === '4' ? '9Mobile' : 'Unknown',
              phoneNumber: formData.phoneNumber,
              amount: parseFloat(formData.amount),
              date: new Date().toLocaleString(),
              reference: response.data.reference || 'N/A'
            });
            setShowReceipt(true);
        }
      } catch (error) {
        setError(error.response?.data?.error || "Transaction failed. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const renderFields = () => {
    if (!["data", "airtime"].includes(serviceType)) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-600 text-lg mb-2">🚧 Service Coming Soon</div>
          <p className="text-gray-500">
            This service is currently under development and will be available soon.
          </p>
        </div>
      );
    }

    const renderedFields = [];

    if (serviceType === "airtime") {
      renderedFields.push(
        <select
          key="network"
          name="network"
          value={formData.network || ""}
          onChange={handleChange}
          className="w-full p-3 border rounded-lg"
          required
        >
          <option value="">Select Network</option>
          <option value="1">MTN</option>
          <option value="3">GLO</option>
          <option value="2">Airtel</option>
          <option value="4">9Mobile</option>
        </select>
      );
    }

    if (serviceType === "data") {
      renderedFields.push(
        <select
          key="planType"
          name="planType"
          value={selectedPlanType}
          onChange={handlePlanTypeChange}
          className="w-full p-3 border rounded-lg"
          required
        >
          <option value="">Select Plan Type</option>
          {allDataPlans.map((plan) => (
            <option key={plan.id} value={plan.name}>
              {plan.name}
            </option>
          ))}
        </select>
      );
    }

    if (serviceType === "data" && dataPlans.length > 0) {
      renderedFields.push(
        <select
          key="dataPlan"
          name="dataPlan"
          value={formData.dataPlan || ""}
          onChange={handleDataPlanSelect}
          className="w-full p-3 border rounded-lg"
          required
        >
          <option value="">Select Data Plan</option>
          {dataPlans.map((plan) => (
            <option key={plan.id} value={plan.id}>
              {`${plan.name} => ₦${parseFloat(plan.reseller_amount).toLocaleString()}`}
            </option>
          ))}
        </select>
      );
    }

    service.fields.forEach((field) => {
      if (
        field !== "network" &&
        field !== "dataPlan" &&
        field !== "planType" &&
        !renderedFields.some((component) => component.key === field)
      ) {
        renderedFields.push(
          <input
            key={field}
            name={field}
            type={field === "amount" ? "number" : "text"}
            value={formData[field] || ""}
            onChange={handleChange}
            placeholder={field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
            className="w-full p-3 border rounded-lg"
            required
          />
        );
      }
    });

    return renderedFields;
  };

  if (!isOpen) return null;

  return (
    <>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between border-b pb-3">
          <h2 className="text-xl font-semibold">{service.name}</h2>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600"
          >
            ✖
          </button>
        </div>
        
        {error && (
          <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

<div className="space-y-4 mt-4">
          {renderFields()}
          {["data", "airtime"].includes(serviceType) && (
            <button 
              onClick={handleSubmit}
              disabled={loading} 
              className={`w-full py-3 rounded-lg text-white transition-colors duration-200 ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-emerald-500 hover:bg-emerald-600'
              }`}
            >
              {loading ? 'Processing...' : 'Proceed to Payment'}
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default ServiceOverlay;